/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import React, { ChangeEvent, useEffect } from 'react'
import { useLang } from '~/hooks/useLang'
import {
  TextField,
  Grid,
  IconButton,
  Typography,
  Divider,
  CircularProgress,
  ListItem,
  ListItemText,
  Box,
  Button,
  Tooltip,
  Collapse,
  Stack,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ExpandLess from '@mui/icons-material/ExpandLess'
import {
  useHookstate,
  createState,
  useState,
  Downgraded,
} from '@hookstate/core'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '~/components/ErrorFallback'
import InfiniteScroll from 'react-infinite-scroll-component'
import useOccurrenceState from '~/features/Occurrence/stores/OccurrenceState'
import useOccurrenceCommentState from '~/features/Occurrence/stores/OccurrenceCommentState'
import useSystemState from '~/features/System/stores/SystemState'
import { Visibility } from '@mui/icons-material'
import { searchWeaponService } from './services'
import dayjs from 'dayjs'

export interface WeaponListProps {
  readonly id: number
  readonly serial_number: string
  readonly occurrence_id: number
  readonly created_at: string
  readonly circunstance: {
    readonly id: number
    readonly name: string
  }
  readonly destination: {
    readonly id: number
    readonly name: string
  }
  readonly weapon_type: {
    readonly id: number
    readonly name: string
  }
  readonly weapon_kind: {
    readonly id: number
    readonly name: string
  }
  readonly occurrence: {
    readonly id: number
    readonly created_at: string
    readonly type: {
      readonly name: string
      readonly code: string
    }
    readonly subtype: {
      readonly name: string
      readonly code: string
    }
    readonly dispatches: ReadonlyArray<{
      readonly id: number
      readonly code: string
    }>
  }
}

const serialNumberState = createState('')

export function useSearchWeapon() {
  const serialNumber = useState(serialNumberState)

  return {
    serialNumber: () => serialNumber,
  }
}

export default function SearchWeapon() {
  const { translate } = useLang()
  const { windowManager } = useSystemState()

  const serialNumberExternal = useSearchWeapon().serialNumber
  const serialNumber = useHookstate('')
  const serialNumberSearch = useHookstate('')
  const weapons = useHookstate<readonly WeaponListProps[]>(
    windowManager().searchWeapon.dataSearchWeapon.attach(Downgraded).get() ?? []
  )
  const weaponsCount = useHookstate(0)
  const { syncOccurrenceEditAttendent } = useOccurrenceState()
  const { syncPreviousComments } = useOccurrenceCommentState()

  const [filtersOpen, setFiltersOpen] = React.useState(true)
  const [height, setHeight] = React.useState(335) 

  useEffect(() => {
    if (filtersOpen) {
      setHeight(335)
    } else {
      const timer = setTimeout(() => {
        setHeight(425)
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [filtersOpen])

  useEffect(() => {
    serialNumber.set(serialNumberExternal().get())
    return () => {
      serialNumberExternal().set('')
    }
  }, [])

  const nextData = async (reset = false) => {
    const pageQuery = reset ? 0 : Math.floor(weapons.get().length / 10)
    searchWeaponService({
      serialNumber: serialNumberSearch.get(),
      page: pageQuery,
      limit: 10,
    }).then(({ data, rowsCount }) => {
      if (reset) {
        weapons.set(data)
        weaponsCount.set(rowsCount)
      } else {
        weapons.merge(data)
        weaponsCount.set(rowsCount)
      }
    })
  }

  const onClick = (id) => {
    syncOccurrenceEditAttendent(id)
    syncPreviousComments(id)
    windowManager()['dispatchesResultSearches'].open.set(true)
  }

  useEffect(() => {
    if (!serialNumber.get()?.length) {
      windowManager()['dispatchesResultSearches'].open.set(false)
    }
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Grid container width={'100%'}>
        <Grid item xs={12}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            pb={1}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{ flexGrow: 1, fontSize: '1rem', fontWeight: 400 }}
            >
              {translate('Filters')}
            </Typography>
            <IconButton
              onClick={() => setFiltersOpen((prev) => !prev)}
              size="small"
              sx={{ 
                ml: 1,
                transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: filtersOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ExpandLess />
            </IconButton>
          </Stack>

          <Collapse 
            in={filtersOpen}
            timeout={{
              enter: 300,
              exit: 300
            }}
            easing={{
              enter: 'cubic-bezier(0.4, 0, 0.2, 1)',
              exit: 'cubic-bezier(0.4, 0, 0.6, 1)'
            }}
          >
            <Grid container xs={12} width={'100%'} direction="row" spacing={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`${translate('Serial Number')}`}
                  size="small"
                  value={serialNumber.get() ?? ''}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    serialNumber.set(event.target.value)
                  }}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
              >
                <Button
                  variant="text"
                  onClick={() => {
                    serialNumber.set('')
                    serialNumberSearch.set('')
                    weapons.set([])
                    weaponsCount.set(0)
                  }}
                  sx={{
                    height: '40px',
                    textTransform: 'none',
                    textDecorationLine: 'underline',
                    color: 'text.primary',
                    ':hover': {
                      bgcolor: 'transparent',
                      color: 'primary.main',
                    },
                  }}
                >
                  {translate('Clear filters')}
                </Button>
                <Tooltip title={translate('Search')} placement="bottom">
                  <IconButton
                    onClick={() => {
                      serialNumberSearch.set(serialNumber.get())
                      nextData(true)
                    }}
                    disabled={serialNumber.get()?.length < 3}
                    aria-label="search-weapon"
                    title={translate('Search')}
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
            <Divider />
          </Collapse>
        </Grid>

        <Grid item xs={12}>
          <InfiniteScroll
            dataLength={weapons.value.length}
            next={() => nextData()}
            hasMore={
              weapons.value.length != weaponsCount.get() &&
              weapons.value.length != 0
            }
            loader={<CircularProgress />}
            style={{
              paddingLeft: 0,
              paddingRight: 0,
              height: height,
              transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {weapons.value.length != 0 ? (
              weapons.get()?.map((items) => (
                <React.Fragment key={items.id}>
                  <ListItem style={{ paddingLeft: 5, paddingRight: 0 }}>
                    <ListItemText
                      primary={`OC: ${
                        items?.occurrence_id
                      } | ${items?.occurrence.dispatches?.map(
                        (item) => `\n\n${item.code}`
                      )}`}
                      secondary={
                        <>
                          <Typography variant={'subtitle2'}>
                            {items?.occurrence.type.name}{' '}
                            {items?.occurrence.subtype?.name}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {translate('Serial Number')}:{items?.serial_number}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {translate('Weapon Type')}:
                            {items?.weapon_type?.name}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {translate('Weapon Kind')}:
                            {items?.weapon_kind?.name}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            {items?.circunstance?.name}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            Destino Final: {items?.destination?.name}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            Data da Ocorrência:{' '}
                            {dayjs(items?.occurrence.created_at).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                          </Typography>
                          <Typography variant={'subtitle2'}>
                            Data do Registro:{' '}
                            {dayjs(items?.created_at).format(
                              'DD/MM/YYYY HH:mm'
                            )}
                          </Typography>
                        </>
                      }
                    />
                    <Box
                      sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        alignItems: 'start',
                      }}
                    >
                      <IconButton
                        aria-label="comment"
                        onClick={() => onClick(items?.occurrence_id)}
                        title="Visualizar"
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </ListItem>
                  <Divider sx={{ marginTop: 2 }} />
                </React.Fragment>
              ))
            ) : (
              <Grid
                item
                xs={12}
                style={{
                  height: height,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 16,
                  boxSizing: 'border-box',
                  transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Typography>{translate('No Weapons')}</Typography>
              </Grid>
            )}
          </InfiniteScroll>
        </Grid>
      </Grid>
    </ErrorBoundary>
  )
}
